import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Users2, Eye, MapPin, Crown, Activity, Search, Filter, X, Loader, TrendingUp } from 'lucide-react';

const GroupManagement = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [cities, setCities] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    city: ''
  });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  const observer = useRef();
  const searchTimeoutRef = useRef();

  // Debounce search input
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [filters.search]);

  // Reset pagination when filters change
  useEffect(() => {
    setGroups([]);
    setPagination(prev => ({ ...prev, page: 1 }));
    setHasMore(true);
    fetchGroups(true);
  }, [debouncedSearch, filters.city]);

  useEffect(() => {
    fetchCities();
  }, []);

  const fetchGroups = async (reset = false) => {
    try {
      const currentPage = reset ? 1 : pagination.page;
      
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const params = new URLSearchParams({
        page: currentPage,
        limit: pagination.limit,
        search: debouncedSearch,
        city: filters.city
      });
      
      const response = await axios.get(`/groups?${params}`);
      
      // Handle the paginated response structure
      let newGroups = [];
      let paginationData = {};
      
      if (response.data && Array.isArray(response.data.groups)) {
        newGroups = response.data.groups;
        paginationData = response.data.pagination || {};
      } else if (response.data && Array.isArray(response.data)) {
        // Fallback for old response format
        newGroups = response.data;
        paginationData = {
          page: currentPage,
          totalPages: 1,
          hasMore: false,
          total: response.data.length
        };
      } else {
        console.warn('Unexpected API response structure:', response.data);
        newGroups = [];
        paginationData = {
          page: currentPage,
          totalPages: 0,
          hasMore: false,
          total: 0
        };
      }

      if (reset) {
        setGroups(newGroups);
      } else {
        setGroups(prevGroups => [...prevGroups, ...newGroups]);
      }

      setPagination({
        page: paginationData.page || currentPage,
        limit: paginationData.limit || 12,
        total: paginationData.total || 0,
        totalPages: paginationData.totalPages || 1
      });

      setHasMore(paginationData.hasMore || false);

    } catch (error) {
      console.error('Error fetching groups:', error);
      if (reset) {
        setGroups([]);
      }
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchCities = async () => {
    try {
      const response = await axios.get('/cities');
      if (response.data && Array.isArray(response.data)) {
        setCities(response.data);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
      setCities([]);
    }
  };

  const viewGroupDetails = (groupId) => {
    navigate(`/groups/${groupId}/details`);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      city: ''
    });
  };

  const loadMoreGroups = useCallback(() => {
    if (hasMore && !loadingMore) {
      setPagination(prev => ({ ...prev, page: prev.page + 1 }));
      fetchGroups(false);
    }
  }, [hasMore, loadingMore, pagination.page]);

  // Infinite scroll observer
  const lastGroupElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loadingMore) {
        loadMoreGroups();
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore, loadingMore, loadMoreGroups]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Group Management</h2>
          <p className="text-gray-600 mt-2">Discover and manage fitness groups across different cities</p>
        </div>
        <div className="flex items-center space-x-3 text-sm text-gray-600">
          <TrendingUp className="h-4 w-4" />
          <span>{pagination.total} groups total</span>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Groups
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by group name..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="input-field pl-10"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by City
            </label>
            <select
              value={filters.city}
              onChange={(e) => setFilters(prev => ({ ...prev, city: e.target.value }))}
              className="input-field"
            >
              <option value="">All Cities</option>
              {cities.map((city) => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>
        </div>
        
        {(filters.search || filters.city) && (
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Filter className="h-4 w-4" />
              <span>Filters applied</span>
              <span>• {groups.length} of {pagination.total} groups shown</span>
            </div>
            <button
              onClick={clearFilters}
              className="btn-secondary text-sm flex items-center"
            >
              <X className="h-4 w-4 mr-1" />
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Groups Grid */}
      <div className="card">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : !groups || groups.length === 0 ? (
          <div className="text-center py-12">
            <Users2 className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No groups found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {filters.search || filters.city
                ? 'Try adjusting your filters to see more groups.'
                : 'No fitness groups are available at the moment.'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {groups.map((group, index) => {
                const isLast = groups.length === index + 1;
                return (
                  <div 
                    key={group.id} 
                    ref={isLast ? lastGroupElementRef : null}
                    className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => viewGroupDetails(group.id)}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">{group.name}</h3>
                      <Users2 className="h-6 w-6 text-blue-600" />
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="h-4 w-4 mr-2" />
                        {group.city}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Users2 className="h-4 w-4 mr-2" />
                        {group.member_count} members
                      </div>
                      {group.hot_stepper_name && (
                        <div className="flex items-center text-sm text-gray-600">
                          <Crown className="h-4 w-4 mr-2 text-yellow-500" />
                          {group.hot_stepper_name}
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="text-center p-3 bg-blue-50 rounded-lg">
                        <div className="text-lg font-bold text-blue-600">
                          {(group.total_steps || 0).toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-600">Steps (7d)</div>
                      </div>
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <div className="text-lg font-bold text-green-600">
                          {(group.total_distance || 0).toFixed(1)} km
                        </div>
                        <div className="text-xs text-gray-600">Distance</div>
                      </div>
                    </div>

                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        viewGroupDetails(group.id);
                      }}
                      className="w-full btn-secondary flex items-center justify-center hover:bg-blue-50 hover:text-blue-600 transition-colors"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View Details
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Loading More Indicator */}
            {loadingMore && (
              <div className="flex items-center justify-center py-8">
                <Loader className="h-6 w-6 animate-spin text-blue-600 mr-2" />
                <span className="text-gray-600">Loading more groups...</span>
              </div>
            )}

            {/* End of Results Indicator */}
            {!hasMore && groups.length > 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500">You've reached the end of the groups list</p>
                <p className="text-sm text-gray-400 mt-1">
                  Showing all {pagination.total} groups
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default GroupManagement; 